import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import {
	CreateLiSn,
	LiSn,
	LiSnPreviewResponse,
	LiSnPreviewResponseSchema,
	LiSnResponse,
	LiSnResponseSchema,
	LiSnSchema,
	LiSnSuggest,
	LiSnSuggestSchema,
	UpdateLiSn,
} from "../../pages/LiSn/schema/liSn";
import { LiSnResultsResponse, LiSnResultsResponseSchema } from "../../pages/LiSn/schema/liSnResult";

export const liSnApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getLiSns: builder.query<LiSnResponse, RequestPaginationParams>({
			query: (params) => ({
				url: "liSn",
				params: {
					...(params && { ...params }),
				},
				responseSchema: LiSnResponseSchema,
			}),
			providesTags: ["LiSn"],
		}),
		getLiSnResults: builder.query<LiSnResultsResponse, RequestPaginationParams & { id: number }>({
			query: ({ id, ...params }) => ({
				url: `liSn/results/${id}`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: LiSnResultsResponseSchema,
			}),
			providesTags: ["LiSn"],
		}),
		getLiSn: builder.query<LiSn, number>({
			query: (id: number) => ({
				url: `liSn/${id}`,
				params: {
					id,
				},
				responseSchema: LiSnSchema,
			}),
			providesTags: ["LiSn"],
		}),
		suggestLiSn: builder.query<
			LiSnSuggest,
			{
				type: string;
				query: string;
			}
		>({
			query: ({ type, query }) => ({
				url: `liSn/suggest/${type}/${query}`,
				responseSchema: LiSnSuggestSchema,
			}),
		}),
		createLiSn: builder.mutation<Partial<LiSn>, CreateLiSn>({
			query: (body) => ({
				url: `liSn`,
				method: "POST",
				body,
				responseSchema: LiSnSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "LiSn"];
				}

				return ["LiSn"];
			},
		}),
		previewLiSn: builder.mutation<LiSnPreviewResponse, CreateLiSn & { limit?: number }>({
			query: ({ limit, ...body }) => ({
				url: `liSn/preview`,
				method: "POST",
				params: { limit: limit ?? 10 },
				body,
				responseSchema: LiSnPreviewResponseSchema,
			}),
		}),
		updateLiSn: builder.mutation<Partial<LiSn>, UpdateLiSn>({
			query: ({ id, ...body }) => ({
				url: `liSn/${id}`,
				method: "PATCH",
				body,
				responseSchema: LiSnSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "LiSn"];
				}

				return ["LiSn"];
			},
		}),
		toggleLiSn: builder.mutation<Partial<LiSn>, Partial<LiSn>>({
			query: ({ id, ...body }) => ({
				url: `liSn/toggle/${id}`,
				method: "PATCH",
				body,
				responseSchema: LiSnSchema,
			}),
			invalidatesTags: ["LiSn"],
		}),
		deleteLiSn: builder.mutation<Partial<LiSn>, { id: number; listId?: number }>({
			query: ({ id }) => ({
				url: `liSn/${id}`,
				method: "DELETE",
				responseSchema: LiSnSchema,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.listId) {
					return [{ type: "List", id: arg.listId }, "LiSn"];
				}

				return ["LiSn"];
			},
		}),
	}),
});

export const {
	useGetLiSnsQuery,
	useGetLiSnResultsQuery,
	useLazyGetLiSnsQuery,
	useGetLiSnQuery,
	useUpdateLiSnMutation,
	useDeleteLiSnMutation,
	useToggleLiSnMutation,
	useCreateLiSnMutation,
	usePreviewLiSnMutation,
	useSuggestLiSnQuery,
	useLazySuggestLiSnQuery,
} = liSnApi;
export const accountApiReducer = liSnApi.reducer;
export const accountApiMiddleware = liSnApi.middleware;
